/**
 * OvertimeApprovals — Wave 3.2 live-API wrapper around <ApprovalList>.
 *
 * Backend data shape for type='overtime' (from seed_test_data.py):
 *   { date, hours, reason }
 *
 * Mock also had start_time/end_time; render them when present so
 * manually-created approvals with richer payloads display correctly.
 */

import React, { useMemo } from 'react';
import { Typography } from '@mui/material';
import ApprovalList, { ApprovalColumn, ApprovalFormField } from './ApprovalList';
import { useTranslation } from 'react-i18next';
// One implementation of the overtime span, shared with ApprovalList's create dialog.
// This file used to keep its own copy and the two disagreed at midnight: the dialog
// rendered nothing for 22:00→02:00 while this column rendered 4 (2026-08-25).
import { endsNextDay, overtimeHours } from './overtimeSpan';

function pickStr(data: unknown, key: string): string {
  return typeof data === 'object' && data !== null && key in data
    ? String((data as Record<string, unknown>)[key] ?? '')
    : '';
}

function pickNum(data: unknown, key: string): number | null {
  if (typeof data !== 'object' || data === null || !(key in data)) return null;
  const v = (data as Record<string, unknown>)[key];
  if (typeof v === 'number') return v;
  if (typeof v === 'string' && v !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

const OvertimeApprovals: React.FC = () => {
  const { t } = useTranslation();

  const columns = useMemo<ApprovalColumn[]>(() => [
    { label: t('attendance:approvals.overtime.colDate'), render: (r) => pickStr(r.data, 'date') || '—' },
    {
      label: t('attendance:approvals.overtime.colTime'),
      render: (r) => {
        const s = pickStr(r.data, 'start_time');
        const e = pickStr(r.data, 'end_time');
        if (!s && !e) return <Typography variant="caption" color="text.disabled">—</Typography>;
        // A span that wraps past midnight says so. Without this the reader has to
        // work out from "22:00 → 02:00" that the end is tomorrow, and the four-hour
        // total next to it looks like a mistake.
        const nextDay = endsNextDay(s, e);
        return (
          <Typography variant="caption">
            {s || '—'} → {e || '—'}
            {nextDay ? ` (${t('attendance:approvals.overtime.endsNextDay')})` : ''}
          </Typography>
        );
      },
    },
    {
      label: t('attendance:approvals.overtime.colHours'),
      align: 'center',
      render: (r) => {
        // `hours` is stored only by THIS surface (the admin's derived/overridable field);
        // the worker portal and mobile write `start_time` / `end_time` and no total. So the
        // column showed "—" for every request they filed. Derive it when it is absent —
        // the two times are what all three surfaces agree on (2026-08-25).
        const h = pickNum(r.data, 'hours') ?? overtimeHours(
          pickStr(r.data, 'start_time'), pickStr(r.data, 'end_time'),
        );
        return h === null ? '—' : (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {h}h
          </Typography>
        );
      },
    },
    {
      label: t('attendance:approvals.overtime.colReason'),
      render: (r) => (
        <Typography variant="caption" color="text.secondary">
          {pickStr(r.data, 'reason') || '—'}
        </Typography>
      ),
    },
  ], [t]);

  const createFields = useMemo<ApprovalFormField[]>(() => [
    { key: 'date', label: t('attendance:approvals.overtime.colDate'), type: 'date', required: true, span: 6 },
    {
      key: 'hours',
      label: t('attendance:approvals.overtime.colHours'),
      type: 'derived_hours',
      required: true,
      span: 6,
      derivedFromTimes: ['start_time', 'end_time'],
    },
    { key: 'start_time', label: t('attendance:approvals.overtime.formStartTime'), type: 'time', required: true, span: 6 },
    { key: 'end_time', label: t('attendance:approvals.overtime.formEndTime'), type: 'time', required: true, span: 6 },
    { key: 'reason', label: t('attendance:approvals.overtime.colReason'), type: 'textarea', required: true },
  ], [t]);

  return (
    <ApprovalList
      type="overtime"
      title={t('attendance:approvals.overtimeTitle')}
      description={t('attendance:approvals.overtimeDescription')}
      recordTitle={t('attendance:approvals.overtime.recordTitle')}
      filterLabel={t('attendance:approvals.filterLabel')}
      columns={columns}
      createFields={createFields}
    />
  );
};

export default OvertimeApprovals;
